import {useAwsRum} from '@nlabs/metropolisjs';
import {useEffect, useLayoutEffect, useState} from 'react';

import {getRoute, scrollToPageTop} from '../lib/navigation';
import {applySeoMetadata, getSeoMetadata} from '../lib/seo';
import {Route} from '../types/routes';
import {About} from '../views/About/About';
import {Coding} from '../views/Coding/Coding';
import {Home} from '../views/Home/Home';
import {ProjectDetail} from '../views/ProjectDetail/ProjectDetail';
import {Projects} from '../views/Projects/Projects';
import {Publishing} from '../views/Publishing/Publishing';
import {Creators} from '../views/Creators/Creators';
import {Portal} from '../views/Portal/Portal';
import {Support} from '../views/Support/Support';

export const App = () => {
  const [route, setRoute] = useState<Route>(getRoute());
  const rum = useAwsRum();

  useEffect(() => {
    const handleRoute = () => setRoute(getRoute());
    window.addEventListener('popstate', handleRoute);
    return () => window.removeEventListener('popstate', handleRoute);
  }, []);

  useLayoutEffect(() => {
    applySeoMetadata(getSeoMetadata(route));
  }, [route]);

  useEffect(() => {
    rum.track({
      name: 'page_view',
      path: route,
      properties: {title: getSeoMetadata(route).title},
      type: 'page_view'
    });
  }, [route, rum]);

  useEffect(() => {
    document.documentElement.classList.add('is-route-jump');
    scrollToPageTop();
    const topResetId = window.setTimeout(scrollToPageTop, 80);
    window.requestAnimationFrame(() => {
      scrollToPageTop();
      document.documentElement.classList.remove('is-route-jump');
    });

    const frameId = window.requestAnimationFrame(() => {
      const revealEls = Array.from(document.querySelectorAll<HTMLElement>('.nx-reveal'));
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if(entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      }, {
        rootMargin: '0px 0px -10% 0px',
        threshold: 0.12
      });

      revealEls.forEach((element) => observer.observe(element));
      (window as any).ufatRevealObserver?.disconnect();
      (window as any).ufatRevealObserver = observer;
    });

    return () => {
      window.cancelAnimationFrame(frameId);
      window.clearTimeout(topResetId);
    };
  }, [route]);

  if(route === '/about') return <About route={route} />;
  if(route.startsWith('/projects/')) return <ProjectDetail route={route} />;
  if(route === '/projects') return <Projects route={route} />;
  if(route === '/coding') return <Coding route={route} />;
  if(route === '/publishing') return <Publishing route={route} />;
  if(route === '/creators') return <Creators route={route} />;
  if(route === '/portal') return <Portal route={route} />;
  if(route === '/support') return <Support route={route} />;
  return <Home route={route} />;
};
